// src/pages/Admin/Payouts/AdminPayoutsPage.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { showToast } from "../../../../../utils/toast";
import {
  adminGetPayoutsAPI,
  adminMarkPaidPayoutAPI,
  adminUploadPayoutReceiptAPI, // ← використовуємо цей
  adminExportPayoutsCSVAPI, // ← і цей (blob) + saveBlob
} from "../../../../../api/payoutsApi";
import {
  download1099CSVAPI,
  download1042SCSVAPI,
  saveBlob,
} from "../../../../../api/taxReportsApi";
import { ChevronDown, SearchIcon, Upload } from "lucide-react";
import { PayoutsTable } from "./PayoutsTable";
import { UserInterface } from "../../../../../types/user";
import AdminPayoutsAndTaxFormsTabs from "../AdminPayoutsAndTaxFormsTabs";

export type PayoutStatus =
  | "pending"
  | "approved"
  | "processing"
  | "paid"
  | "rejected"
  | "canceled";

export type SortByOptions = "amount" | "createdAt" | "paidAt";

export interface IPayoutItem {
  _id: string;
  creatorId: string;
  creator: UserInterface,
  amount: number;
  currency?: string;
  status: PayoutStatus;
  method?: string;
  note?: string;
  providerRef?: string;
  paidAt?: string;
  createdAt?: string;

  destinationMasked?: string;

  receiptMediaUrl?: string;
  receiptOriginalName?: string;

  destination?: Record<string, any>;
}

interface IPayoutListRes {
  items: IPayoutItem[];
  total: number;
  page: number;
  limit: number;
}

export const useStyles = makeStyles((theme) => ({
  container: { padding: 24, color: "#fff" },
  topBar: { display: "flex", gap: 12, alignItems: "center", marginBottom: 12 },
  spacer: { flex: 1 },

  filters: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(140px, 1fr))",
    gap: 12,
    marginBottom: 16,
  },

  tableWrap: {
    background: "#141414",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.08)",
  },
  tableHeaderCell: {
    color: "#fff", fontWeight: 500, fontSize: 16,
    background: "#74747426",
    borderRight: "1px solid #74747426",
    borderBottom: 0,
    lineHeight: "normal",
    height: "auto",
    verticalAlign: "center",

    "&.active": {
      color: "#FF78AA",
    },
    "& .MuiOutlinedInput-inputAdornedStart": {
      background: "transparent",
      border: 0
    }
  },
  tableBodyCell: {
    color: "#fff",
    border: "1px solid #74747426",
    fontSize: 14,
    lineHeight: "normal",
    height: "auto",
  },

  noBorder: { border: 0 },
  searchInput: {
    color: "#fff", marginRight: 8, cursor: "pointer",
  },
  textFieldRoot: {
    "& .MuiOutlinedInput-adornedStart": {
      maxWidth: "165px",
      paddingLeft: 8,
      marginRight: 12
    },
    "& .MuiOutlinedInput-adornedStart, & .MuiInputBase-input": {
      background: "#131313",
      borderRadius: 4,
    },
  },
  inputRoot: { color: "#fff" },
  inputLabel: { color: "rgba(255,255,255,0.9)" },
  outlined: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.35)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.6)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
  },
  menuPaper: { background: "#121212", color: "#fff" },

  paginationRoot: { color: "#fff", fontSize: "16px", },
  paginationCaption: { color: "rgba(255,255,255,0.8)", fontSize: "16px", },
  paginationSelectIcon: { color: "#fff", fontSize: "16px", },

  dialogPaper: { background: "#0f0f0f", color: "#fff" },

  outlinedIconButton: {
    color: "#fff",
    border: "1px solid #74747480",
    borderRadius: 6,
    padding: 8,
    fontSize: 14,
  },

  exportPayoutsButton: {
    color: "#fff",
    fontSize: "20px",
    fontWeight: 600,
    border: "1px solid #FE5A97 !important",
    padding: "12px 20px",
    borderRadius: 6,
    [theme.breakpoints.down("xs")]: {
      fontSize: 12,
      padding: "9px 15px",
    },
  },
}));

// ---------- admin check ----------
type UserLocal = {
  role?: string;
  roles?: string[];
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  userType?: { title?: string };
};
const isUserAdmin = (u?: UserLocal | null) =>
  !!u &&
  (u.isAdmin === true ||
    u.isSuperAdmin === true ||
    String(u.role || "").toLowerCase() === "admin" ||
    String(u.userType?.title || "").toLowerCase() === "admin" ||
    (Array.isArray(u.roles) &&
      u.roles
        .map(r => String(r).toLowerCase())
        .some(
          r => r === "admin" || r === "superadmin" || r === "super-admin"
        )));

const AdminPayoutsPage: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // admin gate
  const [authChecked, setAuthChecked] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("user");
      const parsed = raw ? (JSON.parse(raw) as UserLocal) : null;
      setIsAdminUser(isUserAdmin(parsed));
    } catch {
      setIsAdminUser(false);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  // filters & paging
  const [status, setStatus] = useState<"" | PayoutStatus>("");
  const [creatorUsername, setCreatorUsername] = useState<string>("");
  // const [showCreatorSearch, setShowCreatorSearch] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // data
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<IPayoutItem[]>([]);
  const [total, setTotal] = useState<number>(0);

  // dialogs: mark paid
  const [markPaidOpen, setMarkPaidOpen] = useState<boolean>(false);
  const [markPaidId, setMarkPaidId] = useState<string | null>(null);
  const [providerRef, setProviderRef] = useState<string>("");
  const [note, setNote] = useState<string>("");

  // dialogs: view destination
  const [viewDestOpen, setViewDestOpen] = useState(false);

  // dialogs: upload receipt
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptUploading, setReceiptUploading] = useState(false);

  // dialogs: tax reports (1099 / 1042-S)
  const [taxOpen, setTaxOpen] = useState(false);
  const [taxYear, setTaxYear] = useState<number>(new Date().getFullYear());
  const [taxMin, setTaxMin] = useState<number>(600);
  const [taxLoading, setTaxLoading] = useState<null | "1099" | "1042">(null);

  const load = async (p = page, l = limit, tmpCreatorUsername?: string, tmpStatus?: PayoutStatus | "") => {
    setLoading(true);
    try {
      const res = (await adminGetPayoutsAPI({
        status: tmpStatus !== undefined ? tmpStatus || undefined : status || undefined,
        username: tmpCreatorUsername !== undefined ? tmpCreatorUsername || undefined : creatorUsername || undefined,
        page: p + 1,
        limit: l,
        sortBy,
        sortDir,
      } as any)) as IPayoutListRes;
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error(e);
      showToast({ message: "Failed to load payouts", err: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdminUser) load(0, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminUser]);

  const applyFilters = () => {
    setPage(0);
    load(0, limit);
  };

  const changeSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("desc");
    }

    setPage(0);
    load(0, limit);
  };

  const doMarkPaid = async () => {
    try {
      if (!markPaidId) return;
      await adminMarkPaidPayoutAPI(markPaidId, { providerRef, note });
      showToast({ message: "Marked as paid", success: true });
      setMarkPaidOpen(false);
      load();
    } catch (e) {
      console.error(e);
      showToast({
        message: (e as any)?.message || "Mark paid failed",
        err: true,
      });
    }
  };

  const doUploadReceipt = async () => {
    if (!receiptId || !receiptFile) {
      showToast({ message: "Please choose a file", err: true });
      return;
    }
    setReceiptUploading(true);
    try {
      await adminUploadPayoutReceiptAPI(receiptId, receiptFile);
      showToast({ message: "Receipt uploaded", success: true });
      setReceiptOpen(false);
      load();
    } catch (e) {
      showToast({ message: "Failed to upload receipt", err: true });
    } finally {
      setReceiptUploading(false);
    }
  };

  // export payouts CSV (всі заявки)
  const exportCSV = async () => {
    try {
      const blob = (await adminExportPayoutsCSVAPI({
        status: status || undefined,
        username: creatorUsername || undefined,
        method: undefined,
        from: undefined,
        to: undefined,
      })) as Blob;

      saveBlob(blob, `payouts_${Date.now()}.csv`);
    } catch {
      showToast({ message: "Export failed", err: true });
    }
  };

  // tax reports (1099 / 1042-S)
  const download1099 = async () => {
    setTaxLoading("1099");
    try {
      const blob = (await download1099CSVAPI({
        year: taxYear,
        min: taxMin,
      } as any)) as Blob;
      saveBlob(blob, `1099_candidates_${taxYear}.csv`);
    } catch {
      showToast({ message: "1099 export failed", err: true });
    } finally {
      setTaxLoading(null);
    }
  };
  const download1042 = async () => {
    setTaxLoading("1042");
    try {
      const blob = (await download1042SCSVAPI({ year: taxYear })) as Blob;
      saveBlob(blob, `1042S_candidates_${taxYear}.csv`);
    } catch {
      showToast({ message: "1042-S export failed", err: true });
    } finally {
      setTaxLoading(null);
    }
  };

  // const handleCloseDropdown = (value?: PayoutStatus | "") => {
  //   if (value !== undefined) {
  //     setStatus(value);
  //     setPage(0);
  //     load(0, limit, creatorUsername, value);
  //   }
  // };

  // gates UI
  if (!authChecked) {
    return (
      <Box
        className={classes.container}
        style={{ display: "flex", justifyContent: "center", paddingTop: 40 }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!isAdminUser) {
    return (
      <Box className={classes.container} style={{ textAlign: "center" }}>
        <Typography variant="h5">403 — Admins only</Typography>
        <Typography style={{ opacity: 0.7, marginTop: 8 }}>
          You don&apos;t have access to this page.
        </Typography>
      </Box>
    );
  }

  // main UI
  return (
    <Box className={classes.container}>
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
        }}
      >
        <AdminPayoutsAndTaxFormsTabs />
        {isMobile && (
          <TextField
            autoFocus
            size="small"
            variant="outlined"
            className={classes.textFieldRoot}
            placeholder="Username"
            value={creatorUsername}
            onChange={(e) => setCreatorUsername(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
            InputProps={{
              classes: {
                input: classes.tableHeaderCell,
                notchedOutline: classes.noBorder,
              },
              startAdornment: (
                <InputAdornment position="start" onClick={applyFilters}>
                  <SearchIcon
                    fontSize="small"
                    size={16}
                    className={classes.searchInput}
                  />
                </InputAdornment>
              ),
            }}
          />
        )}

        <IconButton
          size="small"
          onClick={exportCSV}
          className={classes.exportPayoutsButton}
        >
          <Upload style={{ marginRight: 10 }} size={16} />
          Export Payouts CSV
        </IconButton>
      </Box>


      <PayoutsTable
        items={items}
        loading={loading}
        sortBy={sortBy}
        changeSort={changeSort}
        load={load}
        limit={limit}
        status={status}
        setStatus={setStatus}
        setPage={setPage}
        creatorUsername={creatorUsername}
        setCreatorUsername={setCreatorUsername}
        applyFilters={applyFilters}
        setViewDestOpen={setViewDestOpen}
        setReceiptFile={setReceiptFile}
        setReceiptOpen={setReceiptOpen}
        setReceiptId={setReceiptId}
        viewDestOpen={viewDestOpen}
      />


      <Box display="flex" justifyContent="center">
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => {
            setPage(p);
            load(p, limit);
          }}
          rowsPerPage={limit}
          onRowsPerPageChange={e => {
            const v = parseInt((e.target as HTMLInputElement).value, 10);
            setLimit(v);
            setPage(0);
            load(0, v);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          classes={{
            root: classes.paginationRoot,
            caption: classes.paginationCaption,
            selectIcon: classes.paginationSelectIcon,
          }}
          SelectProps={{
            IconComponent: (props) => (
              <ChevronDown size={16} style={{ marginTop: 3 }}
                {...props} />
            ),
            MenuProps: { classes: { paper: classes.menuPaper } }
          }}
        />
      </Box>

      {/* Mark Paid */}
      <Dialog
        open={markPaidOpen}
        onClose={() => setMarkPaidOpen(false)}
        maxWidth="xs"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>Mark payout as paid</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            variant="outlined"
            label="Provider reference"
            value={providerRef}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setProviderRef(e.target.value)
            }
            className={classes.outlined}
            InputProps={{ className: classes.inputRoot }}
            InputLabelProps={{ className: classes.inputLabel, shrink: true }}
            style={{ marginBottom: 12 }}
          />
          <TextField
            fullWidth
            variant="outlined"
            label="Note (optional)"
            value={note}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNote(e.target.value)
            }
            multiline
            rows={3}
            className={classes.outlined}
            InputProps={{ className: classes.inputRoot }}
            InputLabelProps={{ className: classes.inputLabel, shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMarkPaidOpen(false)}
            style={{ color: "#fff" }}
          >
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={doMarkPaid}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Receipt */}
      <Dialog
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        maxWidth="xs"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>Upload receipt</DialogTitle>
        <DialogContent dividers>
          <Typography style={{ marginBottom: 12 }}>
            Attach a payout receipt (PDF/JPG/PNG).
          </Typography>
          <input
            type="file"
            accept=".pdf,image/*"
            onChange={e => setReceiptFile(e.target.files?.[0] || null)}
            style={{ color: "#fff" }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReceiptOpen(false)}
            style={{ color: "#fff" }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={doUploadReceipt}
            disabled={receiptUploading || !receiptFile}
          >
            {receiptUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tax Reports (1099 / 1042-S) */}
      <Dialog
        open={taxOpen}
        onClose={() => setTaxOpen(false)}
        maxWidth="xs"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>Tax Reports</DialogTitle>
        <DialogContent dividers>
          <TextField
            variant="outlined"
            label="Year"
            type="number"
            value={taxYear}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTaxYear(parseInt(e.target.value || "0", 10))
            }
            className={classes.outlined}
            InputProps={{ className: classes.inputRoot }}
            InputLabelProps={{ className: classes.inputLabel, shrink: true }}
            style={{ marginBottom: 12 }}
          />
          <TextField
            variant="outlined"
            label="1099 Min Threshold ($)"
            type="number"
            value={taxMin}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTaxMin(parseFloat(e.target.value || "0"))
            }
            className={classes.outlined}
            InputProps={{ className: classes.inputRoot }}
            InputLabelProps={{ className: classes.inputLabel, shrink: true }}
            style={{ marginBottom: 12 }}
          />
          <Box display="flex">
            <Button
              color="primary"
              variant="contained"
              onClick={download1099}
              disabled={taxLoading === "1099"}
            >
              {taxLoading === "1099" ? "Downloading…" : "Download 1099 CSV"}
            </Button>
            <Button
              variant="outlined"
              onClick={download1042}
              disabled={taxLoading === "1042"}
              style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
            >
              {taxLoading === "1042" ? "Downloading…" : "Download 1042-S CSV"}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaxOpen(false)} style={{ color: "#fff" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPayoutsPage;
